import { useState } from "react";

import type { UsuarioOut } from "../../lib/cadastros/tipos";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Formulário controlado de usuário (FE-4 CP-C). Cobre os DOIS modos:
 *
 *  - CRIAÇÃO (`usuario === undefined`): `email` + `nome` + `senha` (OBRIGATÓRIA) +
 *    `ativo` (default true).
 *  - EDIÇÃO (`usuario` presente): `email` READ-ONLY (identidade de login, não
 *    editável no PATCH) + `nome` + `senha` (OPCIONAL — vazia = "não trocar") +
 *    toggle `ativo`.
 *
 * SENHA WRITE-ONLY (regra dura, ADR 0026 §segredos): nunca exibida nem
 * pré-preenchida; `type="password"` + `autoComplete="new-password"`; nunca logada;
 * a CP-A já garante que a senha JAMAIS entra em queryKey. Na edição, o campo começa
 * VAZIO e o caller só inclui `senha` no payload se o operador digitou uma nova (a
 * intenção `senhaPreenchida` é exposta no `DadosUsuarioForm`).
 *
 * ANTI AUTO-LOCKOUT (UX): o backend devolve 409 se o operador desativar o PRÓPRIO
 * usuário. Quando `ehProprioUsuario` é true, o toggle "ativo" fica DESABILITADO e
 * travado em `true`, com mensagem clara. O 409 do servidor segue tratado como
 * defesa-em-profundidade na tradução de erro.
 *
 * Componente PURO: não conhece o hook de mutação nem o store. O orquestrador
 * injeta `aoSalvar`, `salvando`, o `erro` normalizado e `ehProprioUsuario`. SÓ
 * tokens CPMH; labels em todos os inputs; erro com `role="alert"`.
 */

/**
 * Campos coletados. `senhaPreenchida` distingue "o operador digitou uma senha
 * nova" de "deixou em branco": na edição o caller só envia `senha` quando
 * `senhaPreenchida` é true (vazio = manter a credencial atual).
 */
export interface DadosUsuarioForm {
  email: string;
  nome: string;
  senha: string;
  senhaPreenchida: boolean;
  ativo: boolean;
}

interface FormularioUsuarioProps {
  /** Usuário em edição, ou `undefined` para criação. */
  usuario?: UsuarioOut;
  /**
   * O usuário em edição é o PRÓPRIO operador autenticado? Quando true, o toggle
   * "ativo" fica travado em `true` (anti auto-lockout).
   */
  ehProprioUsuario?: boolean;
  aoSalvar: (dados: DadosUsuarioForm) => void;
  aoCancelar: () => void;
  salvando: boolean;
  erro: unknown;
}

export function FormularioUsuario({
  usuario,
  ehProprioUsuario = false,
  aoSalvar,
  aoCancelar,
  salvando,
  erro,
}: FormularioUsuarioProps): JSX.Element {
  const ehEdicao = usuario !== undefined;

  const [email, setEmail] = useState(usuario?.email ?? "");
  const [nome, setNome] = useState(usuario?.nome ?? "");
  // SENHA WRITE-ONLY: nunca pré-preenchida (nem na edição) — começa sempre vazia.
  const [senha, setSenha] = useState("");
  const [ativo, setAtivo] = useState(usuario?.ativo ?? true);
  const [erroBorda, setErroBorda] = useState<string | null>(null);

  function submeter(): void {
    // Validação de borda: campos obrigatórios.
    if (!ehEdicao && email.trim() === "") {
      setErroBorda("Informe o e-mail.");
      return;
    }
    if (nome.trim() === "") {
      setErroBorda("Informe o nome.");
      return;
    }
    // Na criação a senha é OBRIGATÓRIA; na edição é opcional (vazia = manter).
    if (!ehEdicao && senha === "") {
      setErroBorda("Informe a senha.");
      return;
    }
    setErroBorda(null);
    aoSalvar({
      email: email.trim(),
      nome: nome.trim(),
      senha,
      senhaPreenchida: senha !== "",
      // Anti auto-lockout: o próprio operador nunca submete `ativo=false`.
      ativo: ehProprioUsuario ? true : ativo,
    });
  }

  return (
    <div className="flex max-w-lg flex-col gap-4 rounded-md border border-platinum bg-snow p-6 font-corpo text-base text-off-black">
      <h2 className="font-titulo text-xl text-off-black">
        {ehEdicao ? "Editar usuário" : "Novo usuário"}
      </h2>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">E-mail</span>
        <input
          type="email"
          value={email}
          onChange={(evento) => setEmail(evento.target.value)}
          readOnly={ehEdicao}
          aria-readonly={ehEdicao}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black read-only:bg-platinum"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Nome</span>
        <input
          type="text"
          value={nome}
          onChange={(evento) => setNome(evento.target.value)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">
          {ehEdicao ? "Nova senha (deixe em branco para manter)" : "Senha"}
        </span>
        <input
          type="password"
          autoComplete="new-password"
          value={senha}
          onChange={(evento) => setSenha(evento.target.value)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        />
      </label>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={ativo}
          disabled={ehProprioUsuario}
          onChange={(evento) => setAtivo(evento.target.checked)}
        />
        <span className="font-subtitulo">Ativo</span>
      </label>

      {ehProprioUsuario && (
        <p className="font-corpo text-base text-off-black">
          Você não pode desativar o próprio usuário.
        </p>
      )}

      {erroBorda !== null && (
        <p role="alert" className="font-corpo text-base text-flame">
          {erroBorda}
        </p>
      )}

      {erro !== null && erro !== undefined && (
        <p role="alert" className="font-corpo text-base text-flame">
          {mensagemErroUsuario(erro)}
        </p>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={aoCancelar}
          disabled={salvando}
          className="rounded-md border border-platinum bg-snow px-4 py-1 text-off-black disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submeter}
          disabled={salvando}
          className="rounded-md border border-flame bg-flame px-4 py-1 text-snow disabled:opacity-60"
        >
          {salvando ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </div>
  );
}

/**
 * Traduz o erro de uma mutação de usuário numa mensagem estável. O 409 cobre DOIS
 * conflitos do backend (e-mail duplicado e a defesa anti auto-lockout); a mensagem
 * cita ambos para não vazar qual disparou. 422 (validação — ex.: senha curta)
 * recebe mensagem clara; demais reusam a mensagem genérica do `ErroHttp`. Erro
 * não-`ErroHttp` → fallback genérico. A senha NUNCA aparece em nenhuma mensagem.
 */
function mensagemErroUsuario(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    if (erro.status === 409) {
      return "Conflito: e-mail já cadastrado ou tentativa de desativar o próprio usuário.";
    }
    if (erro.status === 422) {
      return "Dados inválidos. Verifique os campos (a senha deve atender ao tamanho mínimo).";
    }
    return erro.message;
  }
  return "Não foi possível salvar o usuário.";
}
